import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Minus, Plus } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { api, errMsg, todayISO } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ScalePicker, ChipGroup } from "@/components/Pickers";
import { markFirstDone } from "@/components/InstallTip";
import { PageTitle } from "@/components/Common";

const SYMPTOMS = ["cramps", "bloating", "headache", "fatigue", "cravings", "tender breasts", "acne", "back pain", "hot flashes", "low libido"];
const FLOW = ["none", "spotting", "light", "medium", "heavy"];
const MOVE = [0, 10, 20, 30, 45, 60];
const EMPTY = { mood: null, energy: null, sleep_hours: null, sleep_quality: null, stress: null, symptoms: [], cycle_flow: null, period_started: false, water_glasses: null, movement_minutes: null, note: "" };

const Section = ({ title, children, className = "" }) => (
  <section className={`card p-5 sm:p-6 space-y-5 rise ${className}`}>
    <div className="eyebrow">{title}</div>
    {children}
  </section>
);

function CycleSection({ f, set }) {
  return (
    <Section title="Cycle" className="d2">
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold">My period started today</span>
        <Switch aria-label="My period started today" data-testid="checkin-period-started-switch" checked={f.period_started} onCheckedChange={set("period_started")} />
      </div>
      <div>
        <div className="text-sm font-semibold mb-2.5">Flow</div>
        <ChipGroup options={FLOW} multi={false} value={f.cycle_flow} onToggle={(o) => set("cycle_flow")(f.cycle_flow === o ? null : o)} testid="checkin-flow" />
      </div>
    </Section>
  );
}

function HabitsSection({ f, set }) {
  return (
    <Section title="Habits" className="d3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold">Glasses of water</span>
        <div className="flex items-center gap-3">
          <button type="button" aria-label="One less glass of water" data-testid="checkin-water-minus" onClick={() => set("water_glasses")(f.water_glasses == null ? 0 : Math.max(0, f.water_glasses - 1))} className="chip h-11 w-11 grid place-items-center rounded-full border border-stone-200 bg-white"><Minus className="h-4 w-4" /></button>
          <span className="min-w-6 text-center font-semibold" data-testid="checkin-water-value">{f.water_glasses == null ? "–" : f.water_glasses}</span>
          <button type="button" aria-label="One more glass of water" data-testid="checkin-water-plus" onClick={() => set("water_glasses")(Math.min(20, (f.water_glasses || 0) + 1))} className="chip h-11 w-11 grid place-items-center rounded-full border border-stone-200 bg-white"><Plus className="h-4 w-4" /></button>
        </div>
      </div>
      <div>
        <div className="text-sm font-semibold mb-2.5">Movement (minutes)</div>
        <ChipGroup options={MOVE.map(String)} multi={false} value={f.movement_minutes == null ? null : String(f.movement_minutes)}
          onToggle={(o) => set("movement_minutes")(String(f.movement_minutes) === o ? null : Number(o))} testid="checkin-move" />
      </div>
      <textarea data-testid="checkin-note-input" value={f.note || ""} onChange={(e) => set("note")(e.target.value)} maxLength={1000} rows={3}
        placeholder="Anything on your mind? (private, optional)" className="w-full rounded-xl border border-stone-200 p-3.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C86D51]" />
    </Section>
  );
}

export default function CheckIn() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [existing, setExisting] = useState(false);

  useEffect(() => {
    api.get("/today").then(({ data }) => {
      if (data.checkin) {
        setExisting(true);
        setF({ ...EMPTY, ...Object.fromEntries(Object.entries(data.checkin).filter(([, v]) => v !== null)) });
      }
    });
  }, []);

  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const toggleSym = (s) => setF((p) => ({ ...p, symptoms: p.symptoms.includes(s) ? p.symptoms.filter((x) => x !== s) : [...p.symptoms, s] }));

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/checkins", { ...f, date: todayISO(), note: f.note || null });
      markFirstDone();
      toast.success("Check-in saved. Writing today's insight…");
      api.post("/insights/generate").catch(() => {});
      nav("/today", { state: { generating: true } });
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };

  return (
    <div>
      <PageTitle eyebrow={new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
        title={existing ? "Update today's check-in" : "How are you, really?"} sub="About 60 seconds. Every field is optional. Skip what doesn't apply." />
      <div className="space-y-4">
        <Section title="Mind">
          <ScalePicker label="Mood" testid="checkin-mood" value={f.mood} onChange={set("mood")} labels={["Low", "Meh", "Okay", "Good", "Great"]} />
          <ScalePicker label="Stress" testid="checkin-stress" value={f.stress} onChange={set("stress")} labels={["Calm", "Light", "Moderate", "High", "Overwhelmed"]} />
        </Section>
        <Section title="Body" className="d1">
          <ScalePicker label="Energy" testid="checkin-energy" value={f.energy} onChange={set("energy")} labels={["Drained", "Low", "Steady", "Good", "Vibrant"]} />
          <div>
            <div className="flex justify-between mb-3"><span className="text-sm font-semibold">Sleep last night</span>
              <span className="text-sm text-stone-600 flex items-center gap-2" data-testid="checkin-sleep-hours-value">{f.sleep_hours == null ? "Not recorded · slide to add" : `${f.sleep_hours} hours`}
                {f.sleep_hours != null && <button type="button" onClick={() => set("sleep_hours")(null)} data-testid="checkin-sleep-clear" className="min-h-11 px-2 text-xs underline">Clear</button>}</span></div>
            <Slider data-testid="checkin-sleep-hours-slider" aria-label="Hours slept last night" className={f.sleep_hours == null ? "opacity-50" : ""} min={0} max={12} step={0.5} value={[f.sleep_hours ?? 7]} onValueChange={([v]) => set("sleep_hours")(v)} />
          </div>
          <ScalePicker label="Sleep quality" testid="checkin-sleep-quality" value={f.sleep_quality} onChange={set("sleep_quality")} labels={["Restless", "Poor", "Fair", "Good", "Deep"]} />
          <div>
            <div className="text-sm font-semibold mb-2.5">Anything physical?</div>
            <ChipGroup options={SYMPTOMS} value={f.symptoms} onToggle={toggleSym} testid="checkin-symptom" />
          </div>
        </Section>
        {user.cycle_tracking && <CycleSection f={f} set={set} />}
        <HabitsSection f={f} set={set} />
      </div>
      <button disabled={busy} onClick={submit} data-testid="checkin-submit-button" className="btn-terra w-full h-14 rounded-full font-semibold mt-6 disabled:opacity-60">
        {busy ? "Saving…" : existing ? "Update check-in" : "Save check-in"}
      </button>
    </div>
  );
}
